import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import FaqBusinessHoursSection from "./FaqBusinessHoursSection";
import { getPaymentMethodTipoLabel } from "../../constants/paymentMethodTipo";
import { translateSkillForm } from "../../utils/skillLifecycle";
import {
  CATALOG_SCOPE_OPTIONS,
  MERGE_STRATEGY_OPTIONS,
  normalizeProductSellerConfig,
} from "../../utils/productSellerSkillConfig";

function ConfigAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        "&.Mui-expanded": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function ProductSellerSkillConfigSection({
  skillConfig = {},
  mergeSkillConfig,
  readOnly = false,
  canViewPaymentMethods = false,
  paymentMethodsLoading = false,
  paymentMethodsForSelect = [],
  selectedFormaIdStr = "",
  orphanFormaRow = null,
}) {
  const { t } = useTranslation("settings");
  const cfg = normalizeProductSellerConfig(skillConfig);
  const formCopy = (suffix, fallbackKey) => translateSkillForm(t, "product_seller", suffix, fallbackKey);

  return (
    <Stack spacing={1}>
      <ConfigAccordion
        title={formCopy("accordionPersonaTitle", "agentsListPage.productSellerPersonaTitle")}
        subtitle={formCopy("accordionPersonaSubtitle", "agentsListPage.productSellerPersonaSubtitle")}
        defaultExpanded
      >
        <Stack spacing={2}>
          <TextField
            label={formCopy("introMessage", "agentsListPage.productSellerIntroMessage")}
            value={cfg.intro_message}
            onChange={(e) => mergeSkillConfig("product_seller", { intro_message: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={formCopy("salesTone", "agentsListPage.productSellerSalesTone")}
            value={cfg.sales_tone}
            onChange={(e) => mergeSkillConfig("product_seller", { sales_tone: e.target.value })}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={formCopy("salesFocus", "agentsListPage.productSellerSalesFocus")}
            value={cfg.sales_answer_focus}
            onChange={(e) => mergeSkillConfig("product_seller", { sales_answer_focus: e.target.value })}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={formCopy("customInstructions", "agentsListPage.productSellerCustomInstructions")}
            value={cfg.custom_instructions}
            onChange={(e) => mergeSkillConfig("product_seller", { custom_instructions: e.target.value })}
            multiline
            minRows={3}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <FormControl fullWidth size="small" disabled={readOnly}>
            <InputLabel>{formCopy("catalogScope", "agentsListPage.productSellerCatalogScope")}</InputLabel>
            <Select
              label={formCopy("catalogScope", "agentsListPage.productSellerCatalogScope")}
              value={cfg.catalog_scope}
              onChange={(e) => mergeSkillConfig("product_seller", { catalog_scope: e.target.value })}
            >
              {CATALOG_SCOPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={cfg.use_purchase_history}
                onChange={(e) => mergeSkillConfig("product_seller", { use_purchase_history: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={formCopy("usePurchaseHistory", "agentsListPage.productSellerUsePurchaseHistory")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={cfg.cross_sell_enabled}
                onChange={(e) => mergeSkillConfig("product_seller", { cross_sell_enabled: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={formCopy("crossSell", "agentsListPage.productSellerCrossSell")}
          />
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={formCopy("accordionPaymentTitle", "agentsListPage.productSellerPaymentTitle")}
        subtitle={formCopy("accordionPaymentSubtitle", "agentsListPage.productSellerPaymentSubtitle")}
      >
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={cfg.require_email_for_payment}
                onChange={(e) => mergeSkillConfig("product_seller", { require_email_for_payment: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={formCopy("requireEmail", "agentsListPage.productSellerRequireEmail")}
          />
          {canViewPaymentMethods ? (
            <FormControl fullWidth size="small" disabled={readOnly || paymentMethodsLoading}>
              <InputLabel>{t("agentsListPage.skillParamsIdFormaPagamento")}</InputLabel>
              <Select
                label={t("agentsListPage.skillParamsIdFormaPagamento")}
                value={selectedFormaIdStr}
                onChange={(e) => {
                  const v = e.target.value;
                  mergeSkillConfig("product_seller", {
                    default_payment_method_id: v === "" ? "" : String(v),
                  });
                }}
              >
                <MenuItem value="">
                  <em>{t("agentsListPage.skillParamsFormaPagamentoDefault")}</em>
                </MenuItem>
                {paymentMethodsForSelect.map((m) => (
                  <MenuItem key={m.id} value={String(m.id)}>
                    {m.nome || "—"} — {getPaymentMethodTipoLabel(m.tipo)} (ID {m.id})
                  </MenuItem>
                ))}
                {selectedFormaIdStr &&
                  !paymentMethodsForSelect.some((m) => String(m.id) === selectedFormaIdStr) && (
                    <MenuItem value={selectedFormaIdStr}>
                      {orphanFormaRow
                        ? `${orphanFormaRow.nome || "—"} (ID ${orphanFormaRow.id})`
                        : t("agentsListPage.skillParamsFormaPagamentoUnknownId", { id: selectedFormaIdStr })}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label={t("agentsListPage.skillParamsIdFormaPagamentoManual")}
              value={cfg.default_payment_method_id}
              onChange={(e) =>
                mergeSkillConfig("product_seller", { default_payment_method_id: e.target.value.replace(/\D/g, "") })
              }
              fullWidth
              size="small"
              disabled={readOnly}
            />
          )}
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={formCopy("accordionOmnichannelTitle", "agentsListPage.productSellerOmnichannelTitle")}
        subtitle={formCopy("accordionOmnichannelSubtitle", "agentsListPage.productSellerOmnichannelSubtitle")}
      >
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={cfg.omnichannel_enabled}
                onChange={(e) => mergeSkillConfig("product_seller", { omnichannel_enabled: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={formCopy("omnichannelEnabled", "agentsListPage.productSellerOmnichannelEnabled")}
          />
          <TextField
            label={formCopy("mergeMaxAge", "agentsListPage.productSellerMergeMaxAge")}
            type="number"
            value={cfg.merge_cart_max_age_hours}
            onChange={(e) =>
              mergeSkillConfig("product_seller", {
                merge_cart_max_age_hours: Math.max(1, Math.min(168, Number(e.target.value) || 48)),
              })
            }
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <FormControl fullWidth size="small" disabled={readOnly}>
            <InputLabel>{formCopy("mergeStrategy", "agentsListPage.productSellerMergeStrategy")}</InputLabel>
            <Select
              label={formCopy("mergeStrategy", "agentsListPage.productSellerMergeStrategy")}
              value={cfg.merge_strategy}
              onChange={(e) => mergeSkillConfig("product_seller", { merge_strategy: e.target.value })}
            >
              {MERGE_STRATEGY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={formCopy("ecomResumeUrl", "agentsListPage.productSellerEcomResumeUrl")}
            value={cfg.ecom_resume_base_url}
            onChange={(e) => mergeSkillConfig("product_seller", { ecom_resume_base_url: e.target.value })}
            placeholder="https://loja.exemplo.com.br"
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={formCopy("resumeCartMessage", "agentsListPage.productSellerResumeCartMessage")}
            value={cfg.resume_cart_message}
            onChange={(e) => mergeSkillConfig("product_seller", { resume_cart_message: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={t("agentsListPage.formSkillAccordionHoursTitle")}
        subtitle={t("agentsListPage.formSkillAccordionHoursHint")}
      >
        <FaqBusinessHoursSection
          businessHours={cfg.business_hours}
          outsideHoursMessage={cfg.outside_hours_message}
          onChange={(partial) => mergeSkillConfig("product_seller", partial)}
          readOnly={readOnly}
        />
      </ConfigAccordion>
    </Stack>
  );
}
